/**
 * LegalTermsPage — エールチャット 利用規約
 *
 * B2B SaaS 想定の一般的な利用規約構成。日本国内の電気通信事業法、消費者契約法、
 * 不正アクセス禁止法、著作権法等の遵守を前提に、利用契約・禁止事項・免責・準拠法
 * を網羅する。
 *
 * ライトテーマ専用。design tokens を直接使用し、Tailwind の標準クラスのみで
 * 長文ドキュメント向けに可読性を確保する。
 *
 * ルート: `/legal/terms`
 */
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export function LegalTermsPage(): JSX.Element {
  return (
    <div className="min-h-screen bg-surface-base">
      <main
        id="legal-main"
        className="mx-auto w-full max-w-3xl px-4 py-10 sm:px-6 sm:py-16"
        aria-labelledby="legal-terms-heading"
      >
        <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-brand-700">
          Legal / 利用規約
        </p>
        <h1
          id="legal-terms-heading"
          className="mb-2 text-3xl font-bold leading-tight text-fg-primary"
        >
          利用規約
        </h1>
        <p className="mb-8 text-sm text-fg-muted">
          最終改定日: 2026年9月8日
        </p>

        <article className="space-y-8 text-base leading-relaxed text-fg-primary">
          <section>
            <h2 className="mb-3 text-xl font-bold text-fg-primary">
              第1条 (目的)
            </h2>
            <p className="text-fg-secondary">
              本利用規約（以下「本規約」といいます。）は、エールチャット（以下「当社」といいます。）が提供する個人・法人向けコミュニケーションサービス「エールチャット」（以下「本サービス」といいます。）の利用条件を、本サービスを利用する個人、法人および各ワークスペースに参加する利用者（以下総称して「ユーザー」といいます。）と当社との間で定めるものです。
              ユーザーは本規約に同意のうえ、本サービスを利用するものとします。
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-fg-primary">
              第2条 (定義)
            </h2>
            <p className="mb-3 text-fg-secondary">
              本規約において、以下の用語は次の意味で使用します。
            </p>
            <ul className="list-disc space-y-1.5 pl-6 text-fg-secondary">
              <li>
                <strong className="text-fg-primary">契約者:</strong>{" "}
                本サービスの利用申込を行い、当社と利用契約を締結した個人または法人
              </li>
              <li>
                <strong className="text-fg-primary">利用者:</strong>{" "}
                契約者自身、および契約者によりワークスペースの利用を許諾された個人
              </li>
              <li>
                <strong className="text-fg-primary">利用契約:</strong>{" "}
                契約者と当社との間に成立する、本サービスの利用に関する契約
              </li>
              <li>
                <strong className="text-fg-primary">ユーザーコンテンツ:</strong>{" "}
                利用者が本サービスを通じて投稿、送信、保存するメッセージ、添付ファイル、タスク、通話録音その他の情報
              </li>
              <li>
                <strong className="text-fg-primary">プラン:</strong>{" "}
                本サービスの提供範囲、利用上限、料金等を定めた契約形態
              </li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-fg-primary">
              第3条 (利用契約の成立)
            </h2>
            <p className="text-fg-secondary">
              利用契約は、契約者が当社所定の方法により申込を行い、当社がこれを承諾した時点で成立します。当社は、申込内容に虚偽がある場合、過去に本規約違反による契約解除を受けた者からの申込である場合、その他当社が不適当と判断した場合、申込を承諾しないことがあります。
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-fg-primary">
              第4条 (アカウント管理)
            </h2>
            <ul className="list-disc space-y-1.5 pl-6 text-fg-secondary">
              <li>契約者は、利用者ごとに個別のアカウントを発行し、認証情報を厳重に管理する責任を負います。</li>
              <li>認証情報を第三者に共有、貸与、譲渡することはできません。</li>
              <li>アカウントの不正利用が判明した場合、契約者は直ちに当社に通知するものとします。</li>
              <li>当社は、認証情報が一致してなされた操作を、当該アカウントの利用者本人による操作とみなします。</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-fg-primary">
              第5条 (料金および支払)
            </h2>
            <ul className="list-disc space-y-1.5 pl-6 text-fg-secondary">
              <li>個人ワークスペースは広告付き無料です。履歴・添付・検索索引は 90 日間保持されます。チャット・タスク・ファイル・AI に月次の利用枠はなく、人数・総保存容量のプラン上限はありません。ビデオ通話は 1 対 1・毎月 10 分無料で、追加分は 30 日間有効の前払いトッピングを利用できます。単体ファイルの容量や 1 回の処理件数などの技術上の条件、および不正利用を防ぐための制御は各操作で案内します。</li>
              <li>法人ワークスペースは広告なしの有料区分です。標準料金は 1 人あたり月 500 円、年払いは年 5,000 円（月換算約 417 円）、いずれも税別です。当社の人材紹介をご契約の企業には、契約確認後、月 250 円 / 人・年払い 3,000 円 / 人（税別）の料金を適用します。標準プランと同じ機能・通話・録画の含有枠を利用できます。販売・請求設定が未稼働の間は、画面の料金案内のみで請求や有料契約は開始しません。既存の契約は、その契約条件を維持します。</li>
              <li>利用区分はワークスペース単位で所有者が選択し、招待された利用者にも適用します。法人契約の終了によって個人区分へ自動変更することはなく、法人ワークスペースに広告は表示しません。</li>
              <li>個人ワークスペースの広告には広告表示とスポンサー名を付けます。実際のスポンサー契約がない場合には表示しません。広告閲覧をメッセージ送信や機能利用の条件にはしません。</li>
              <li>支払方法および請求サイクルは、利用契約締結時または別途当社が定める手続に従うものとします。</li>
              <li>支払を遅滞した場合、契約者は年14.6%の遅延損害金を当社に支払うものとします。</li>
              <li>当社が消費税相当額を含む金額を請求する場合、契約者は消費税相当額を負担するものとします。</li>
              <li>支払済みの料金は、当社の責に帰すべき事由による場合を除き、返金しません。</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-fg-primary">
              第6条 (禁止事項)
            </h2>
            <p className="mb-3 text-fg-secondary">
              ユーザーは、本サービスの利用にあたり、以下の行為を行ってはなりません。
            </p>
            <ul className="list-disc space-y-1.5 pl-6 text-fg-secondary">
              <li>法令、公序良俗に違反する行為</li>
              <li>犯罪行為に関連する行為</li>
              <li>当社または第三者の知的財産権、肖像権、プライバシー権、名誉その他の権利または利益を侵害する行為</li>
              <li>本サービスのサーバーまたはネットワークに過度の負荷を与える行為</li>
              <li>本サービスの運営を妨害するおそれのある行為</li>
              <li>不正アクセス、認証情報の不正取得、リバースエンジニアリング</li>
              <li>マルウェア、ウィルス、有害なコードを送信する行為</li>
              <li>本サービスを介して反社会的勢力に対する利益供与を行う行為</li>
              <li>他のユーザーに対するハラスメント、誹謗中傷、差別的言動</li>
              <li>当社が事前に許諾しない方法での本サービスの再販、再ライセンス、第三者への提供</li>
              <li>その他、当社が不適切と判断する行為</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-fg-primary">
              第7条 (ユーザーコンテンツ)
            </h2>
            <ul className="list-disc space-y-1.5 pl-6 text-fg-secondary">
              <li>ユーザーコンテンツの権利は、当該コンテンツを投稿したユーザーまたは当該ユーザーが所属する契約者に帰属します。</li>
              <li>ユーザーは、ユーザーコンテンツについて、当社が本サービスの提供、改善、不正検知、法令対応の目的で必要な範囲で利用、複製、保存、変更することを当社に許諾します。</li>
              <li>当社は、法令違反または本規約違反のおそれがあると判断したユーザーコンテンツを、事前の通知なく削除または非表示にすることができます。</li>
              <li>ユーザーコンテンツに起因する第三者との紛争は、当該ユーザーおよびその所属する契約者の責任と費用で解決するものとします。</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-fg-primary">
              第8条 (サービスの変更・中断・終了)
            </h2>
            <p className="text-fg-secondary">
              当社は、本サービスの内容を予告なく変更することがあります。また、システムの保守点検、第三者による不正アクセス、天災地変、その他当社の合理的支配の及ばない事由により、本サービスの全部または一部の提供を中断または停止することがあります。当社は、これらに起因してユーザーに生じた損害について、本規約第10条 (免責) の範囲で責任を負います。
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-fg-primary">
              第9条 (利用契約の解除)
            </h2>
            <ul className="list-disc space-y-1.5 pl-6 text-fg-secondary">
              <li>契約者は、当社所定の手続に従い、利用契約を解約することができます。</li>
              <li>当社は、契約者または利用者が本規約に違反した場合、催告なく利用契約を解除することができます。</li>
              <li>利用契約が終了した場合、当社は契約終了から90日が経過した時点で、契約者および利用者のユーザーコンテンツを削除する権利を有します。</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-fg-primary">
              第10条 (免責)
            </h2>
            <ul className="list-disc space-y-1.5 pl-6 text-fg-secondary">
              <li>当社は、本サービスについて、特定の目的への適合性、商品性、完全性、正確性、有用性、第三者の権利を侵害しないことを保証しません。</li>
              <li>当社の責に帰すべき事由によりユーザーに損害が生じた場合、当社は、損害の直接の原因となった事象が発生した月を含む過去12ヶ月間に契約者が当社に支払った本サービスの料金総額を上限として、ユーザーに生じた直接かつ通常の損害についてのみ責任を負います。</li>
              <li>逸失利益、特別損害、間接損害、データ滅失に起因する損害について、当社は責任を負いません。</li>
              <li>前2項の制限は、当社に故意または重過失がある場合には適用されません。</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-fg-primary">
              第11条 (秘密保持)
            </h2>
            <p className="text-fg-secondary">
              当社およびユーザーは、本サービスの利用に関連して相手方から開示された営業上または技術上の情報のうち、相手方が秘密である旨を明示した情報、または書面・口頭の別を問わず性質上秘密であることが明らかな情報を、相手方の事前の書面による同意なく第三者に開示してはなりません。秘密保持義務は、利用契約終了後も3年間存続します。
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-fg-primary">
              第12条 (反社会的勢力の排除)
            </h2>
            <p className="text-fg-secondary">
              ユーザーは、自らが暴力団、暴力団員、暴力団準構成員、暴力団関係企業、総会屋、社会運動標榜ゴロ、政治活動標榜ゴロ、特殊知能暴力集団等（以下総称して「反社会的勢力」といいます。）でないこと、および反社会的勢力との間に資金提供その他の関係を有していないことを表明し、保証します。当社は、ユーザーが本条に違反したことが判明した場合、催告なく利用契約を解除することができます。
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-fg-primary">
              第13条 (準拠法および合意管轄)
            </h2>
            <p className="text-fg-secondary">
              本規約の準拠法は日本法とします。本規約または本サービスに関連してユーザーと当社との間に紛争が生じた場合、東京地方裁判所を第一審の専属的合意管轄裁判所とします。
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-fg-primary">
              第14条 (お問い合わせ先)
            </h2>
            <address className="not-italic rounded-md border border-border-subtle bg-canvas p-4 text-sm text-fg-secondary">
              <div className="font-bold text-fg-primary">エールチャット カスタマーサポート</div>
              <div className="mt-1">メールアドレス: support@skillbridge.co.jp</div>
              <div>受付時間: 平日 10:00 – 18:00 (日本時間 / 土日祝・年末年始を除く)</div>
            </address>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-fg-primary">
              第15条 (改定履歴)
            </h2>
            <ul className="list-disc space-y-1.5 pl-6 text-fg-secondary">
              <li>2026年9月8日 - 現在の料金・個人90日保持・ビデオ通話条件との整合を修正</li>
              <li>2026年9月6日 - 個人・法人の利用区分、料金、スポンサー広告の方針を更新</li>
              <li>2026年5月20日 — 初版公開</li>
            </ul>
            <p className="mt-3 text-fg-secondary">
              当社は、必要に応じて本規約を変更することがあります。重要な変更を行う場合は、本サービス内の通知またはメールにより、ユーザーに事前にお知らせします。
            </p>
          </section>
        </article>

        <footer className="mt-12 border-t border-border-subtle pt-6">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 rounded px-1 py-0.5 text-sm text-brand-700 hover:underline focus-visible:shadow-focus"
          >
            <ArrowLeft size={14} aria-hidden /> ログイン画面に戻る
          </Link>
        </footer>
      </main>
    </div>
  );
}
